import { useCallback, useEffect, useRef } from "react";

const AXIS_THRESHOLD_PX = 10;

export function useDragSelect({ enabled, isSelected, onSelect, onDeselect, getItemById }) {
    const draggingRef = useRef(false);
    const scrollIntentRef = useRef(false);
    const visitedRef = useRef(new Set());
    const modeRef = useRef("select");
    const startPointRef = useRef(null);
    const pointerIdRef = useRef(null);

    const applyToItem = useCallback(
        (itemId, item) => {
            if (!item || visitedRef.current.has(itemId)) {
                return;
            }
            visitedRef.current.add(itemId);

            if (modeRef.current === "select") {
                onSelect(item);
            } else {
                onDeselect(item);
            }
        },
        [onDeselect, onSelect]
    );

    const resolveItemFromPoint = useCallback(
        (clientX, clientY) => {
            const target = document.elementFromPoint(clientX, clientY);
            const node = target?.closest("[data-delivery-select-id]");
            if (!node) {
                return null;
            }
            const itemId = node.getAttribute("data-delivery-select-id");
            if (!itemId) {
                return null;
            }
            const item = getItemById(itemId);
            return item ? { itemId, item } : null;
        },
        [getItemById]
    );

    const endDrag = useCallback(() => {
        draggingRef.current = false;
        scrollIntentRef.current = false;
        visitedRef.current = new Set();
        startPointRef.current = null;
        pointerIdRef.current = null;
    }, []);

    const resolveGesture = useCallback((clientX, clientY) => {
        const start = startPointRef.current;
        if (!start || draggingRef.current || scrollIntentRef.current) {
            return;
        }

        const dx = Math.abs(clientX - start.x);
        const dy = Math.abs(clientY - start.y);

        if (dx < AXIS_THRESHOLD_PX && dy < AXIS_THRESHOLD_PX) {
            return;
        }

        if (dy >= AXIS_THRESHOLD_PX && dy > dx) {
            scrollIntentRef.current = true;
            return;
        }

        if (dx >= AXIS_THRESHOLD_PX && dx >= dy) {
            draggingRef.current = true;
            const resolved = resolveItemFromPoint(clientX, clientY);
            if (resolved) {
                applyToItem(resolved.itemId, resolved.item);
            }
        }
    }, [applyToItem, resolveItemFromPoint]);

    const handlePointerDown = useCallback(
        (item, itemId, event) => {
            if (!enabled || event.button > 0) {
                return;
            }

            startPointRef.current = { x: event.clientX, y: event.clientY };
            pointerIdRef.current = event.pointerId;
            scrollIntentRef.current = false;
            draggingRef.current = false;
            visitedRef.current = new Set();
            modeRef.current = isSelected(itemId) ? "deselect" : "select";
        },
        [enabled, isSelected]
    );

    const handlePointerMove = useCallback(
        (item, itemId, event) => {
            if (!enabled || pointerIdRef.current !== event.pointerId) {
                return;
            }

            if (!startPointRef.current) {
                return;
            }

            if (!draggingRef.current && !scrollIntentRef.current) {
                resolveGesture(event.clientX, event.clientY);
            }

            if (scrollIntentRef.current || !draggingRef.current) {
                return;
            }

            const resolved = resolveItemFromPoint(event.clientX, event.clientY);
            if (resolved) {
                applyToItem(resolved.itemId, resolved.item);
            } else {
                applyToItem(itemId, item);
            }
        },
        [applyToItem, enabled, resolveGesture, resolveItemFromPoint]
    );

    const handlePointerEnter = useCallback(
        (item, itemId, event) => {
            if (
                !enabled ||
                scrollIntentRef.current ||
                !draggingRef.current ||
                pointerIdRef.current !== event.pointerId
            ) {
                return;
            }
            applyToItem(itemId, item);
        },
        [applyToItem, enabled]
    );

    const handlePointerUp = useCallback(
        (item, itemId, event) => {
            if (!enabled || pointerIdRef.current !== event.pointerId) {
                return false;
            }

            const wasDragging = draggingRef.current;
            const wasScroll = scrollIntentRef.current;
            endDrag();
            return !wasDragging && !wasScroll;
        },
        [enabled, endDrag]
    );

    useEffect(() => {
        if (!enabled) {
            endDrag();
            return undefined;
        }

        const handleGlobalPointerMove = (event) => {
            if (pointerIdRef.current !== event.pointerId || !startPointRef.current) {
                return;
            }

            if (!draggingRef.current && !scrollIntentRef.current) {
                resolveGesture(event.clientX, event.clientY);
            }

            if (scrollIntentRef.current || !draggingRef.current) {
                return;
            }

            const resolved = resolveItemFromPoint(event.clientX, event.clientY);
            if (resolved) {
                applyToItem(resolved.itemId, resolved.item);
            }
        };

        const handleGlobalPointerUp = () => {
            endDrag();
        };

        window.addEventListener("pointermove", handleGlobalPointerMove);
        window.addEventListener("pointerup", handleGlobalPointerUp);
        window.addEventListener("pointercancel", handleGlobalPointerUp);
        return () => {
            window.removeEventListener("pointermove", handleGlobalPointerMove);
            window.removeEventListener("pointerup", handleGlobalPointerUp);
            window.removeEventListener("pointercancel", handleGlobalPointerUp);
        };
    }, [applyToItem, enabled, endDrag, resolveGesture, resolveItemFromPoint]);

    return {
        handlePointerDown,
        handlePointerMove,
        handlePointerEnter,
        handlePointerUp,
    };
}
